/**
 * What one tab needs to tell the others about a design.
 *
 * Every page of this app is a separate document sharing one IndexedDB, and
 * until now none of them knew the others existed. Deleting a design on
 * `/app/projects` removed the row and the archive, and an editor open on that
 * same design in another tab carried on holding its id — so the next brush
 * stroke wrote the whole thing back, name and all. A delete that undoes itself
 * a minute later is worse than one that fails, and nothing on screen in either
 * tab gave any sign of it.
 *
 * `BroadcastChannel` rather than the `storage` event: it delivers structured
 * messages, it does not require writing to storage as a side channel, and it
 * does not fire in the tab that sent it — which is exactly the semantics here,
 * since the tab doing the deleting has already handled it.
 *
 * Absent in a few environments (older Safari, and the node test suite), so
 * every entry point degrades to doing nothing. Losing cross-tab coordination
 * costs the single-tab guarantees nothing: the pointer clearing in
 * `current-project.ts` already covers one tab on its own.
 */

const CHANNEL = "rcm:projects";

export type ProjectEvent =
  /** Gone from this device, and from the account if it was ever in one. */
  { kind: "deleted"; id: string };

function open(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
}

/**
 * Tell the other tabs.
 *
 * Fire-and-forget, and deliberately not awaited by callers: a delete has
 * already happened by the time this runs, and a browser that cannot broadcast
 * must not turn a completed delete into a reported failure.
 */
export function announce(event: ProjectEvent): void {
  const channel = open();
  if (!channel) return;
  try {
    channel.postMessage(event);
  } catch {
    // Structured-clone failure or a closing channel. Nothing to do: the local
    // half of the operation has already succeeded.
  } finally {
    channel.close();
  }
}

/** Listen until the returned function is called. */
export function onProjectEvent(handler: (event: ProjectEvent) => void): () => void {
  const channel = open();
  if (!channel) return () => {};

  const listener = (e: MessageEvent) => {
    const data = e.data as ProjectEvent | undefined;
    // Another origin's message, or a future version of this app sending
    // something this build does not understand.
    if (!data || typeof data !== "object" || data.kind !== "deleted") return;
    if (typeof data.id !== "string" || !data.id) return;
    handler(data);
  };

  channel.addEventListener("message", listener);
  return () => {
    channel.removeEventListener("message", listener);
    channel.close();
  };
}
